// Painéis do shell: período, perfil ("Ver como"), busca global e assistente.
import { useState } from "react";
import type { Period, Role } from "@/api/types";
import { ROLES, isInternal } from "@/access/permissions";
import { switchRole } from "@/auth/session";
import { useOverlay } from "@/ui/overlay";
import { Icon } from "@/ui/Icon";
import { useConsole, PERIOD_LABELS } from "./ConsoleContext";
import { allowedRoutes } from "./routes";
import { navigate } from "./hashRoute";

/** "Ver como" troca persona fictícia: só existe enquanto os dados vêm do mock. */
const DEMO_PERSONAS = import.meta.env.VITE_MOCKS === "on";

export function usePeriodPanel() {
  const { openPanel } = useOverlay();
  return () => openPanel("Escolha o período", <PeriodPanel />);
}

function PeriodPanel() {
  const c = useConsole();
  const { closePanel, toast } = useOverlay();
  const [from, setFrom] = useState(c.scope.from ?? "2026-10-23");
  const [to, setTo] = useState(c.scope.to ?? "2026-10-29");
  const quick: Period[] = ["yesterday", "7d", "30d"];
  return (
    <>
      <div className="chips">
        {quick.map((p) => (
          <button
            key={p}
            className={c.scope.period === p ? "selected" : ""}
            onClick={() => {
              c.setPeriod(p);
              closePanel();
            }}
          >
            {PERIOD_LABELS[p]}
          </button>
        ))}
      </div>
      <label>
        Data inicial
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
      </label>
      <label>
        Data final
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
      </label>
      <button
        className="primary"
        onClick={() => {
          if (from > to) {
            toast("A data final deve ser posterior à inicial");
            return;
          }
          c.setPeriod("custom", { from, to });
          closePanel();
        }}
      >
        Aplicar período personalizado
      </button>
    </>
  );
}

export function useProfilePanel() {
  const { openPanel } = useOverlay();
  return () => openPanel("Sua conta", <ProfilePanel />);
}

function ProfilePanel() {
  const c = useConsole();
  const { closePanel, toast } = useOverlay();
  const scope = isInternal(c.me) ? "Todas as organizações" : c.me.units.includes("all") ? "Todas as unidades" : c.me.units.map(c.unitName).join(", ");
  return (
    <>
      <div className="rolehint">
        <b>{c.me.name}</b>
        <p>{c.me.email}</p>
        <p className="muted">
          {c.me.role} · {c.me.org === "internal" ? "Equipe Iuvo" : c.orgName(c.me.org)}
        </p>
      </div>
      <p className="muted">Escopo: {scope}</p>
      <button
        className="logout-button"
        onClick={() => {
          closePanel();
          c.logout();
        }}
      >
        Sair da conta <Icon name="logout" />
      </button>
      {DEMO_PERSONAS && (
        <>
          <label>
            Ver como · demonstração:
            <select
              value={c.me.role}
              onChange={async (e) => {
                try {
                  const me = await switchRole(e.target.value as Role);
                  closePanel();
                  c.replaceMe(me);
                  toast(`Ver como: ${me.role}`);
                } catch {
                  toast("Não há usuário ativo neste perfil para a demonstração");
                }
              }}
            >
              {ROLES.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </label>
          <p className="muted">O seletor acima troca personagens fictícios para apresentar os diferentes acessos do protótipo.</p>
        </>
      )}
      <div className="callout">Permissões e sessões demonstrativas. O acesso real exige validação no servidor.</div>
    </>
  );
}

export function useGlobalSearch() {
  const { openPanel } = useOverlay();
  return () => openPanel("Buscar no console", <GlobalSearch />);
}

function GlobalSearch() {
  const c = useConsole();
  const { closePanel } = useOverlay();
  const [q, setQ] = useState("");
  const routes = allowedRoutes(c.me).filter((r) => r.label.toLowerCase().includes(q.toLowerCase()));
  const protocol = /^[a-z]{2}-\d+$/i.test(q.trim()) && c.me.permissions.includes("conversations");
  return (
    <>
      <input autoFocus placeholder="Tela, agente ou protocolo…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div>
        {routes.map((r) => (
          <div className="metricrow" key={r.slug}>
            <button
              className="link"
              onClick={() => {
                closePanel();
                navigate(r.slug);
              }}
            >
              {r.label} ↗
            </button>
          </div>
        ))}
        {protocol && (
          <button
            onClick={() => {
              closePanel();
              navigate("conversas", { id: q.trim().toUpperCase() });
            }}
          >
            Abrir atendimento {q.trim().toUpperCase()}
          </button>
        )}
      </div>
    </>
  );
}

/** Assistente: o painel completo é carregado sob demanda (chunk próprio). */
export function useAssistant() {
  const { openPanel } = useOverlay();
  return () => {
    void import("@/features/assistant").then(({ default: Assistant }) => openPanel("Assistente Iuvo", <Assistant />, undefined, { bare: true }));
  };
}
